import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import {
  organizationService,
  ORGANIZATION_API_PATHS,
} from '../services/organization.service'
import type {
  OrganizationListResponse,
  GetOrganizationListRequest,
  SaveOrUpdateOrganizationRequest,
  DepartmentListResponse,
  GetDepartmentListRequest,
  SaveOrUpdateDepartmentRequest,
  DepartmentTreeResponse,
  GetDepartmentTreeRequest,
  GetDepartmentTreeToAgentRequest,
  DepartmentTreeToAgentResponse,
  MemberListResponse,
  GetMemberListRequest,
  MoveMembersRequest,
  SaveOrUpdateMemberRequest,
  DownloadTemplateResponse,
  ImportDepartmentRequest,
  ImportMemberRequest,
} from '../types/organization.type'

/**
 * 获取组织列表 Hook
 * @param params 查询参数（工作空间ID、搜索关键词、创建人等）
 * @returns SWR 查询对象，包含 data、error、mutate 等
 */
export const useOrganizationList = (params: GetOrganizationListRequest) => {
  // 构建唯一的key，使用 JSON.stringify 确保对象参数变化时 key 能正确更新
  const key = [ORGANIZATION_API_PATHS.LIST, JSON.stringify(params)]

  return useSWR<OrganizationListResponse>(
    key,
    () => organizationService.getOrganizationList(params),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )
}

/**
 * 删除组织 Hook
 * @returns SWR mutation 对象，包含 trigger、isMutating、error
 */
export const useDeleteOrganization = () => {
  return useSWRMutation<boolean, Error, string, string>(
    ORGANIZATION_API_PATHS.DELETE,
    (_, { arg }) => organizationService.deleteOrganization(arg)
  )
}

/**
 * 新增/更新组织 Hook
 * @returns SWR mutation 对象，包含 trigger、isMutating、error
 */
export const useSaveOrUpdateOrganization = () => {
  return useSWRMutation<
    boolean,
    Error,
    string,
    SaveOrUpdateOrganizationRequest
  >(ORGANIZATION_API_PATHS.SAVE_OR_UPDATE, (_, { arg }) =>
    organizationService.saveOrUpdateOrganization(arg)
  )
}

/**
 * 切换组织状态 Hook
 * @returns SWR mutation 对象，包含 trigger、isMutating、error
 */
export const useToggleOrganizationState = () => {
  return useSWRMutation<boolean, Error, string, { id: string; status: number }>(
    ORGANIZATION_API_PATHS.TOGGLE_STATE,
    (_, { arg }) =>
      organizationService.toggleOrganizationState(arg.id, arg.status)
  )
}

/**
 * 组织操作组合 Hook
 * 提供完整的组织管理功能，包括列表获取、新增、编辑、删除、状态切换
 * 所有操作完成后会自动刷新列表
 * @param params 列表查询参数
 * @returns 包含所有组织操作方法的对象
 */
export const useOrganization = (params: GetOrganizationListRequest) => {
  const { data, error, mutate } = useOrganizationList(params)
  const { trigger: saveOrUpdateOrg, isMutating: isSaving } =
    useSaveOrUpdateOrganization()
  const { trigger: deleteOrg, isMutating: isDeleting } = useDeleteOrganization()
  const { trigger: toggleState, isMutating: isToggling } =
    useToggleOrganizationState()

  /**
   * 新增或更新组织
   * @param orgData 组织数据（新增时不传id，编辑时必传id）
   * @returns Promise<boolean>
   */
  const handleSaveOrUpdateOrg = async (
    orgData: SaveOrUpdateOrganizationRequest
  ) => {
    const result = await saveOrUpdateOrg(orgData)
    // 操作成功后刷新列表
    await mutate()
    return result
  }

  /**
   * 删除组织
   * @param id 组织ID
   * @returns Promise<boolean>
   */
  const removeOrg = async (id: string) => {
    const result = await deleteOrg(id)
    // 操作成功后刷新列表
    await mutate()
    return result
  }

  /**
   * 切换组织状态
   * @param id 组织ID
   * @param newStatus 新状态
   * @returns Promise<boolean>
   */
  const toggleOrgStatus = async (id: string, newStatus: number) => {
    const result = await toggleState({ id, status: newStatus })
    // 操作成功后刷新列表
    await mutate()
    return result
  }

  return {
    // 列表数据
    organizationList: data?.teams || [],
    isLoading: !data && !error,
    error,

    // 操作方法
    saveOrUpdateOrg: handleSaveOrUpdateOrg,
    removeOrg,
    toggleOrgStatus,

    // 加载状态
    isSaving,
    isDeleting,
    isToggling,

    // 刷新方法
    refresh: mutate,
  }
}

/**
 * 获取部门列表 Hook
 */
export const useDepartmentList = (params: GetDepartmentListRequest) => {
  const key = [ORGANIZATION_API_PATHS.DEPT_LIST, JSON.stringify(params)]
  return useSWR<DepartmentListResponse>(
    key,
    () => organizationService.getDepartmentList(params),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  )
}

/**
 * 新增/更新部门 Hook
 */
export const useSaveOrUpdateDepartment = () => {
  return useSWRMutation<boolean, Error, string, SaveOrUpdateDepartmentRequest>(
    ORGANIZATION_API_PATHS.DEPT_SAVE_OR_UPDATE,
    (_, { arg }) => organizationService.saveOrUpdateDepartment(arg)
  )
}

/**
 * 删除部门 Hook
 */
export const useDeleteDepartment = () => {
  return useSWRMutation<boolean, Error, string, string>(
    ORGANIZATION_API_PATHS.DEPT_DELETE,
    (_, { arg }) => organizationService.deleteDepartment(arg)
  )
}

/**
 * 获取部门树 Hook
 */
export const useDepartmentTree = (params: GetDepartmentTreeRequest) => {
  const key = [ORGANIZATION_API_PATHS.DEPT_TREE, JSON.stringify(params)]
  return useSWR<DepartmentTreeResponse>(
    key,
    () => organizationService.getDepartmentTree(params),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  )
}

/**
 * 获取智能体下部门树 Hook
 */
export const useDepartmentTreeToAgent = (params: GetDepartmentTreeToAgentRequest) => {
  const key = [ORGANIZATION_API_PATHS.DEPT_TREE_TO_AGENT, JSON.stringify(params)]
  return useSWR<DepartmentTreeToAgentResponse>(
    key,
    () => organizationService.getDepartmentTreeToAgent(params),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  )
}

/**
 * 部门操作组合 Hook
 */
export const useDepartment = (teamId: string, parentId: string = '0') => {
  const { data, error, mutate } = useDepartmentList({ teamId, parentId })
  const { trigger: saveOrUpdateDept, isMutating: isSaving } =
    useSaveOrUpdateDepartment()
  const { trigger: deleteDept, isMutating: isDeleting } = useDeleteDepartment()

  const saveOrUpdateDepartment = async (
    payload: SaveOrUpdateDepartmentRequest
  ) => {
    const result = await saveOrUpdateDept(payload)
    await mutate()
    return result
  }

  const removeDepartment = async (id: string) => {
    const result = await deleteDept(id)
    await mutate()
    return result
  }

  return {
    departments: data?.departments || [],
    isLoading: !data && !error,
    error,
    saveOrUpdateDepartment,
    removeDepartment,
    isSaving,
    isDeleting,
    refresh: mutate,
  }
}

/**
 * 成员列表 Hook
 */
export const useMemberList = (
  params?: GetMemberListRequest,
  isRoot?: boolean
) => {
  const key = [
    isRoot
      ? ORGANIZATION_API_PATHS.ALL_MEMBER_LIST
      : ORGANIZATION_API_PATHS.MEMBER_LIST,
    JSON.stringify(params),
  ]
  return useSWR<MemberListResponse>(
    key,
    () => organizationService.getMemberList(params!, isRoot!),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  )
}

/**
 * 批量删除成员 Hook
 */
export const useDeleteMembers = () => {
  return useSWRMutation<boolean, Error, string, string[]>(
    ORGANIZATION_API_PATHS.MEMBER_DELETE_BATCH,
    (_, { arg }) => organizationService.deleteMembers(arg)
  )
}

/**
 * 移动成员 Hook
 */
export const useMoveMembers = () => {
  return useSWRMutation<boolean, Error, string, MoveMembersRequest>(
    ORGANIZATION_API_PATHS.MEMBER_MOVE,
    (_, { arg }) => organizationService.moveMembers(arg)
  )
}

/**
 * 新增或编辑成员 Hook
 */
export const useSaveOrUpdateMember = () => {
  return useSWRMutation<boolean, Error, string, SaveOrUpdateMemberRequest>(
    ORGANIZATION_API_PATHS.MEMBER_SAVE_OR_UPDATE,
    (_, { arg }) => organizationService.saveOrUpdateMember(arg)
  )
}

/**
 * 成员操作组合 Hook
 */
export const useMembers = (params: GetMemberListRequest) => {
  const { data, error, mutate } = useMemberList(params)
  const { trigger: deleteBatch, isMutating: isDeleting } = useDeleteMembers()
  const { trigger: move, isMutating: isMoving } = useMoveMembers()
  const { trigger: saveOrUpdate, isMutating: isSaving } =
    useSaveOrUpdateMember()

  const deleteMembers = async (ids: string[]) => {
    const result = await deleteBatch(ids)
    await mutate()
    return result
  }

  const moveMembers = async (payload: MoveMembersRequest) => {
    const result = await move(payload)
    await mutate()
    return result
  }

  const saveOrUpdateMember = async (payload: SaveOrUpdateMemberRequest) => {
    const result = await saveOrUpdate(payload)
    await mutate()
    return result
  }

  return {
    members: data?.members || [],
    total: data?.total || 0,
    isLoading: !data && !error,
    error,
    deleteMembers,
    moveMembers,
    saveOrUpdateMember,
    isDeleting,
    isMoving,
    isSaving,
    refresh: mutate,
  }
}

/**
 * 下载部门导入模板 Hook
 */
export const useDownloadDepartmentTemplate = () => {
  return useSWRMutation<DownloadTemplateResponse, Error, string, void>(
    ORGANIZATION_API_PATHS.DEPT_IMPORT_TEMPLATE,
    () => organizationService.downloadDepartmentTemplate()
  )
}

/**
 * 导入部门 Hook
 */
export const useImportDepartment = () => {
  return useSWRMutation<boolean, Error, string, ImportDepartmentRequest>(
    ORGANIZATION_API_PATHS.DEPT_IMPORT,
    (_, { arg }) => organizationService.importDepartment(arg)
  )
}

/**
 * 下载成员导入模板 Hook
 */
export const useDownloadMemberTemplate = () => {
  return useSWRMutation<DownloadTemplateResponse, Error, string, void>(
    ORGANIZATION_API_PATHS.MEMBER_IMPORT_TEMPLATE,
    () => organizationService.downloadMemberTemplate()
  )
}

/**
 * 导入成员 Hook
 */
export const useImportMember = () => {
  return useSWRMutation<boolean, Error, string, ImportMemberRequest>(
    ORGANIZATION_API_PATHS.MEMBER_IMPORT,
    (_, { arg }) => organizationService.importMember(arg)
  )
}
